import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { computed, Inject, Injectable, PLATFORM_ID, Signal } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { from, lastValueFrom, Observable, of, retry, throwError } from 'rxjs';
import { AppConfigService } from 'sharedServices/app-config.service';
import { selectLeadNumber } from 'stores/quote/quote.selectors';
import { selectAuthToken } from 'stores/auth/auth.selectors';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { DataLayer } from 'sharedGlobals/gtm-data-layer/models/data-layer.model';
import { Events } from 'sharedGlobals/gtm-data-layer/enums/events.enum';
import { CreateLeadService } from 'services/api/create-lead.service';
import {
    selectAbTestName,
    selectActivePage,
} from 'sharedStores/quote-controller/quote-controller.selectors';
import * as AuthActions from 'stores/auth/auth.actions';
import { QuoteActions, QuoteSelectors } from 'stores/quote/quote.module';
import { ScriptInjectionService } from 'sharedGlobals/gtm-data-layer/script-injection.service';
import { DynamicPageData } from 'sharedModels/dynamic-page-data';
import { ErrorDialogService } from 'sharedGlobals/errors/error-dialog.service';
import { ErrorDialogComponent } from 'sharedGlobals/errors/error-dialog/error-dialog.component';
import { Router } from '@angular/router';
import { LoggingService } from 'sharedServices/logging.service';
import { clearError, requestFailure } from 'sharedStores/error/error.actions';
import { PendingRequestsService } from 'shared/base/generics/services/pending-requests.service';

@UntilDestroy()
@Injectable()
export class ApiInterceptor implements HttpInterceptor {
    private readonly leadNumber = this.store$.selectSignal(selectLeadNumber);
    private readonly token = this.store$.selectSignal(selectAuthToken);
    private readonly headerWhiteList: Signal<Array<string>>;

    constructor(
        @Inject(PLATFORM_ID) private readonly platformId: Object,
        private readonly store$: Store,
        private readonly appConfig: AppConfigService,
        private readonly createLeadService$: CreateLeadService,
        private readonly gtmService$: ScriptInjectionService,
        private readonly errorDialogService: ErrorDialogService,
        private readonly router: Router,
        private readonly loggingService: LoggingService,
        private readonly pendingRequestsService: PendingRequestsService,
    ) {
        this.headerWhiteList = computed(() => {
            return [
                ...this.appConfig.config().headerWhiteList,
                this.appConfig.config().apiUrl,
                this.appConfig.config().cmsUrl,
            ];
        });
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // We only do the custom interceptor logic for urls in our white list, e.g. api and cms url, we don't want this
        // to happen on anny http request (e.g. ignore exceptionless calls)
        if (
            req.headers.get('skip') ||
            !this.headerWhiteList()?.some((value) => req.url.includes(value))
        ) {
            return next.handle(req);
        }

        return from(this.handle(req, next));
    }

    async handle(req: HttpRequest<any>, next: HttpHandler): Promise<HttpEvent<any>> {
        // get quote step using url
        let headers: any = { 'x-quoteStep': this.router.url };

        let leadNumber: string = this.leadNumber();
        let token: string = this.token();
        const page = this.store$.selectSignal(selectActivePage);

        if (this.shouldCreateLead(page(), req, leadNumber)) {
            const createLeadRes = await this.createLead(leadNumber, token);
            leadNumber = createLeadRes.leadNumber;
            token = createLeadRes.token;
        }

        if (leadNumber && req.headers.get('encryptedLead') !== 'true') {
            headers = { ...headers, referenceNumber: leadNumber };
        }

        // If we have a jwt token, add it to the Auth header
        if (token) {
            headers = { ...headers, Authorization: `Bearer ${token}` };
        }

        const newRequest = req.clone({
            setHeaders: headers,
        });

        return this.handleHttpRequest(next, newRequest, leadNumber);
    }

    private shouldCreateLead(pageData: DynamicPageData, req: HttpRequest<any>, leadNumber: string) {
        const noLead = req.headers.get('no-lead');
        return (
            !noLead &&
            isPlatformBrowser(this.platformId) &&
            leadNumber == null &&
            pageData?.page?.createLead === true &&
            !req.url.includes('CreateLead')
        );
    }

    private async createLead(leadNumber: string, token: string) {
        try {
            const { utm_medium, utm_source, utm_campaign, vdn } = this.store$.selectSignal(
                QuoteSelectors.selectQueryParameters,
            )();
            const { googleClientId, googleClickId } = this.gtmService$.getGoogleTrackingDetails();
            const abTestId = this.store$.selectSignal(selectAbTestName)();

            const serviceResponse = await lastValueFrom(
                this.createLeadService$.createLead({
                    vdn,
                    utmMedium: utm_medium,
                    utmSource: utm_source,
                    utmCampaign: utm_campaign,
                    googleClientId,
                    googleClickId,
                    abTestId,
                }),
            );
            leadNumber = serviceResponse.leadNumber;
            token = serviceResponse.token;
            this.store$.dispatch(AuthActions.userAuthenticated({ token: serviceResponse.token }));
            this.store$.dispatch(
                QuoteActions.createLeadResponse({
                    leadNumber: serviceResponse.leadNumber,
                    userId: serviceResponse.userId,
                    vdn: serviceResponse.vdn,
                    googleClientId: googleClientId,
                    googleClickId: googleClickId,
                }),
            );
        } catch (error) {
            this.store$.dispatch(requestFailure(error));
        }
        return { leadNumber, token };
    }

    private handleServiceError(error: any, leadNumber: string, httpRequest: HttpRequest<any>) {
        this.loggingService.logFeature({
            featureName: 'User Dropoff after API Error',
            tags: [leadNumber],
            additionalInfo: { name: 'error', value: error },
        });
        return of();
    }

    private handleHttpRequest(
        next: HttpHandler,
        httpRequest: HttpRequest<any>,
        leadNumber: string,
    ) {
        this.pendingRequestsService.start();

        let start = 0;
        if (isPlatformBrowser(this.platformId)) {
            start = window.performance.now();
        }

        return lastValueFrom(
            next.handle(httpRequest).pipe(
                retry({
                    count: this.appConfig.config().retryLimit,
                    delay: (error) => {
                        const { title, message } = this.appConfig.getErrorTitleAndMessage(
                            error,
                            true,
                        );

                        this.loggingService.logException({
                            error,
                            tags: ['Retry', 'API'],
                            critical: true,
                            additionalInfo: { name: 'httpRequest', value: httpRequest },
                        });
                        const dialogModal = this.errorDialogService.openDialog(
                            title,
                            message,
                            error.status,
                            true,
                        );

                        const instance = dialogModal.getContentComponent() as ErrorDialogComponent;

                        this.store$.dispatch(clearError());

                        return instance.retry.pipe(
                            switchMap((retry: boolean) => {
                                this.errorDialogService.closeDialog();

                                if (retry) {
                                    this.loggingService.log({
                                        message: 'Retrying API request',
                                        tags: ['Retry', 'API'],
                                        level: 'Info',
                                        additionalInfo: [
                                            { name: 'httpRequest', value: httpRequest },
                                        ],
                                    });
                                } else {
                                    this.loggingService.log({
                                        message: 'Closing Error Popup - Callback request',
                                        tags: ['Retry', 'API'],
                                        level: 'Info',
                                        additionalInfo: [
                                            { name: 'httpRequest', value: httpRequest },
                                        ],
                                    });
                                    this.store$.dispatch(clearError());
                                }

                                return retry ? of(error) : throwError(() => error);
                            }),
                        );
                    },
                }),
                catchError((error) => {
                    return this.handleServiceError(error, leadNumber, httpRequest);
                }),
                finalize(() => {
                    this.pendingRequestsService.stop();

                    // Client side and Server side has different handling - we only log performance on client side
                    if (isPlatformBrowser(this.platformId)) {
                        const elapsed = (window?.performance.now() - start) / 1000;
                        new DataLayer({
                            event: Events.API,
                            policyNumber: leadNumber,
                        })
                            .setCustomProperty('endPoint', httpRequest.url)
                            .setCustomProperty('method', httpRequest.method)
                            .setCustomProperty('elapsedTime', `${elapsed.toFixed(2)}s`)
                            .submit();
                    }
                }),
            ),
        );
    }
}
